import {
  getPublicActionSessionId,
  isCurrentPublicAction,
  PublicShareMethod,
  trackPublicShareCompleted,
} from '~publish/helpers/publicTracking'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { GuestMedia } from '~publish/helpers/guestMedia'

import { getGuestMediaAccess } from './guestMediaClient'

export type CopyState = 'idle' | 'copied' | 'error'

export function useCopyText(
  text: string,
  hasMedia = false,
): {
  state: CopyState
  copy: () => Promise<void>
} {
  const [state, setState] = useState<CopyState>('idle')

  useEffect(() => {
    setState('idle')
  }, [text])

  const copy = useCallback(async (): Promise<void> => {
    const sessionId = getPublicActionSessionId()
    try {
      await navigator.clipboard.writeText(text)
      setState('copied')
      if (isCurrentPublicAction(sessionId))
        trackPublicShareCompleted(PublicShareMethod.COPY_TEXT, hasMedia)
    } catch {
      setState('error')
    }
  }, [text, hasMedia])

  return { state, copy }
}

export type MediaSaveStatus = 'saving' | 'saved' | 'error'

function startDownload(accessUrl: string, fileName: string): void {
  const link = document.createElement('a')
  link.href = accessUrl
  link.download = fileName
  link.rel = 'noopener'
  link.hidden = true
  document.body.append(link)
  link.click()
  link.remove()
}

export function useGuestMediaSaver(media: readonly GuestMedia[]): {
  statuses: ReadonlyMap<string, MediaSaveStatus>
  failedCount: number
  isSavingAll: boolean
  allSaved: boolean
  save: (asset: GuestMedia) => Promise<void>
  saveAll: () => Promise<void>
} {
  const [statuses, setStatuses] = useState<
    ReadonlyMap<string, MediaSaveStatus>
  >(new Map())
  const [isSavingAll, setIsSavingAll] = useState(false)
  const controllerRef = useRef<AbortController>()

  useEffect(
    () => (): void => {
      controllerRef.current?.abort()
    },
    [],
  )

  const setStatus = useCallback(
    (mediaId: string, status: MediaSaveStatus): void => {
      setStatuses((current) => new Map(current).set(mediaId, status))
    },
    [],
  )

  const getSignal = useCallback((): AbortSignal => {
    controllerRef.current ??= new AbortController()
    return controllerRef.current.signal
  }, [])

  const save = useCallback(
    async (asset: GuestMedia): Promise<void> => {
      const sessionId = getPublicActionSessionId()
      const signal = getSignal()
      setStatus(asset.id, 'saving')
      try {
        const { accessUrl } = await getGuestMediaAccess(asset, {
          download: true,
          signal,
        })
        if (signal.aborted) return
        startDownload(accessUrl, asset.fileName)
        setStatus(asset.id, 'saved')
        if (isCurrentPublicAction(sessionId))
          trackPublicShareCompleted(PublicShareMethod.DOWNLOAD_MEDIA, true)
      } catch {
        if (signal.aborted) return
        setStatus(asset.id, 'error')
      }
    },
    [getSignal, setStatus],
  )

  const saveAll = useCallback(async (): Promise<void> => {
    setIsSavingAll(true)
    await Promise.all(media.map(save))
    setIsSavingAll(false)
  }, [media, save])

  const failedCount = media.filter(
    (asset) => statuses.get(asset.id) === 'error',
  ).length
  const allSaved =
    media.length > 0 &&
    media.every((asset) => statuses.get(asset.id) === 'saved')

  return { statuses, failedCount, isSavingAll, allSaved, save, saveAll }
}
